import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Gauge } from "lucide-react";
import * as React from "react";

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Skeleton,
  selectClass,
} from "@/components/ui/primitives";
import { ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import { getEvaluationSettings, putEvaluationSettings } from "@/lib/api/chat";
import { useLlmModels } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import type { EvaluationSettings } from "@/types/api";

/**
 * Automatic scoring (AVI-170), org admins only. Off until turned on: each
 * scored answer costs one judge call, so the admin also picks the share of
 * answers to score and the model that judges them.
 */
export function ScoringSettings() {
  const q = useQuery({ queryKey: qk.evaluationSettings, queryFn: getEvaluationSettings });
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (q.isLoading || !q.data) return <Skeleton className="h-48 w-full" />;
  return <ScoringCard current={q.data} />;
}

function ScoringCard({ current }: { current: EvaluationSettings }) {
  const qc = useQueryClient();
  const models = useLlmModels();
  const [enabled, setEnabled] = React.useState(current.enabled);
  const [rate, setRate] = React.useState(String(current.sample_rate));
  const [judge, setJudge] = React.useState(current.judge_llm_model_slug ?? "");
  const [threshold, setThreshold] = React.useState(String(current.retrieval_threshold));
  const [showUsers, setShowUsers] = React.useState(current.show_to_users);

  React.useEffect(() => {
    setEnabled(current.enabled);
    setRate(String(current.sample_rate));
    setJudge(current.judge_llm_model_slug ?? "");
    setThreshold(String(current.retrieval_threshold));
    setShowUsers(current.show_to_users);
  }, [current]);

  const rateN = Number(rate);
  const thresholdN = Number(threshold);
  const valid =
    Number.isInteger(rateN) &&
    rateN >= 1 &&
    rateN <= 100 &&
    threshold.trim() !== "" &&
    thresholdN >= 0 &&
    thresholdN <= 1;

  const save = useMutation({
    mutationFn: () =>
      putEvaluationSettings({
        enabled,
        sample_rate: rateN,
        judge_llm_model_slug: judge || null,
        retrieval_threshold: thresholdN,
        show_to_users: showUsers,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.evaluationSettings });
      toast.success(enabled ? "Automatic scoring is on." : "Settings saved.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2 space-y-0">
        <Gauge className="h-4 w-4 text-primary" />
        <CardTitle className="text-base">Automatic scoring</CardTitle>
        <Badge variant={current.enabled ? "success" : "secondary"}>
          {current.enabled ? `On · ${current.sample_rate}% of answers` : "Off"}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          Score chat answers that were built on data a tool or knowledge base returned, after they
          are delivered: High, Medium or Low and a % accurate, with the judge&apos;s reason and a
          suggested fix. Answers with no data to check against (general knowledge, a decline, a
          clarifying question) are not scored. Each scored answer costs one judge call, shown in
          Audit logs. An evaluator&apos;s 👍 or 👎 overrides the level.
        </p>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-input accent-primary"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          Score answers automatically
        </label>

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="sc-rate">Answers to score (%)</Label>
            <Input
              id="sc-rate"
              type="number"
              min={1}
              max={100}
              value={rate}
              onChange={(e) => setRate(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">A random sample of finished answers.</p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="sc-judge">Judge model</Label>
            <select
              id="sc-judge"
              className={selectClass}
              value={judge}
              onChange={(e) => setJudge(e.target.value)}
            >
              <option value="">Each agent version&apos;s own model</option>
              {(models.data ?? []).map((m) => (
                <option key={m.slug} value={m.slug}>
                  {m.name} ({m.model})
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">From your LLM providers.</p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="sc-threshold">Weak KB match below</Label>
            <Input
              id="sc-threshold"
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">Scored Low without a judge call.</p>
          </div>
        </div>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
            checked={showUsers}
            onChange={(e) => setShowUsers(e.target.checked)}
          />
          <span>
            Show accuracy badges to users
            <span className="block text-xs text-muted-foreground">
              Admins, engineers and evaluators always see the High / Medium / Low badge on answers
              in the chat app.
            </span>
          </span>
        </label>

        <div className="flex justify-end">
          <Button disabled={save.isPending || !valid} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
